import React, { useMemo } from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import { DialogTitle as MuiDialogTitle, DialogContent as MuiDialogContent, DialogContentText, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, styled, Chip, Typography } from '@material-ui/core'
import { Clear } from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useForm, useFormState } from 'react-final-form';
import { orderBy } from 'lodash'

import AutocompleteInput from '../../../common/inputs/AutocompleteInput';
import { useIsOwnParticipant } from '../../../common/hooks/hooks';
import { useQualificationsWithSettings } from '../useQualificationsWithSettings';
import { isAuthorized } from '../../../common/Authorize';

const DialogTitle = styled(MuiDialogTitle)({
  position: 'sticky',
  top: 0,
  background: 'white',
  zIndex: 10,
})

const DialogContent = styled(MuiDialogContent)({
  paddingTop: 0,
})

const NewChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.success[500],
  borderColor: theme.palette.success[500],
  backgroundColor: theme.palette.success[50],
  marginLeft: theme.spacing(1),
  height: theme.spacing(2.5),
}))

const AssignerSelectionAlert = ({ allOrgsAllowAssignerSelection = false, noOrgsAllowAssignerSelection = false }) => {
  const translate = useTranslate();

  if (allOrgsAllowAssignerSelection) return null;

  const title = noOrgsAllowAssignerSelection
    ? 'resources.participants.alerts.no_assigner_selection_title'
    : 'resources.participants.alerts.limited_assigner_selection_title';

  const message = noOrgsAllowAssignerSelection
    ? 'resources.participants.alerts.no_assigner_selection_message'
    : 'resources.participants.alerts.limited_assigner_selection_message';

  return <Alert severity="warning">
    <AlertTitle>{translate(title)}</AlertTitle>
    {translate(message)}
  </Alert>
}

const Office = ({ office, initialSelected = false, canEdit = false }) => {
  const translate = useTranslate();
  const { mutators } = useForm();
  const { values: { officeIds } } = useFormState();

  const removeOffice = () => {
    if (!canEdit) return;
    const index = officeIds.findIndex(value => value === office.id);
    mutators.remove('officeIds', index);
  }

  return <ListItem dense component="li">
    <ListItemText
      primary={<>
        <Typography variant="inherit">{office?.name || translate('ra.message.unknown')}</Typography>
        {!initialSelected && <NewChip size="small" label={translate('ra.action.new')} />}
      </>}
      secondary={office?.parent?.name}
    />
    {canEdit && <ListItemSecondaryAction>
      <IconButton edge="end" onClick={removeOffice}>
        <Clear />
      </IconButton>
    </ListItemSecondaryAction>}
  </ListItem>
}

const Selection = ({ offices = [], excludedIds = [], canEdit = false, isAssigner = false }) => {
  const translate = useTranslate();
  const isOwnParticipant = useIsOwnParticipant();
  const { values: { officeIds }, initialValues } = useFormState();

  if (!officeIds?.length) {
    return <Alert severity="error">
      <AlertTitle>{translate('resources.participants.alerts.availability.no_associations.title')}</AlertTitle>
      <p>{translate(`resources.participants.alerts.availability.no_associations.${isOwnParticipant ? 'own_profile' : 'other_profile'}.search_make_yourself_available`)}</p>
      <p>{translate(`resources.participants.alerts.availability.no_associations.${isOwnParticipant ? 'own_profile' : 'other_profile'}.not_available_to_assigners`)}</p>
    </Alert>
  }

  let selection = officeIds.map(officeId => offices.find(office => office.id === officeId)).filter(Boolean)
  selection = orderBy(selection, ['parent.name', 'name'])

  const initialSelection = initialValues?.officeIds || [];

  return <List>
    {selection.map(office => {
      const initialSelected = initialSelection.includes(office.id);
      const isEditable = (canEdit && !excludedIds.includes(office.id)) || isAssigner;
      return <Office office={office} initialSelected={initialSelected} canEdit={isEditable} />
    })}
  </List>
}

const OfficesForm = ({ participantId, offices }) => {
  const translate = useTranslate();
  const { data: qualifications = [] } = useQualificationsWithSettings(participantId);
  const isOwnParticipant = useIsOwnParticipant(participantId);
  const record = useRecordContext();

  const isAssigner = isAuthorized(record, 'officialAvailabilities', 'assigners');

  const allOrgsAllowAssignerSelection = useMemo(() => (
    qualifications?.length > 1 ? qualifications.every(q => q.office?.branchSettings?.allowOfficialSetAssignOffices) : true
  ), [qualifications]);

  const noOrgsAllowAssignerSelection = useMemo(() => (
    qualifications?.length > 1 ? qualifications.every(q => !q.office?.branchSettings?.allowOfficialSetAssignOffices) : false
  ), [qualifications]);

  const includedOfficeIds = useMemo(() => (
    qualifications.filter(q => q.office?.branchSettings?.allowOfficialSetAssignOffices).map(q => q.office?.branchSettings?.branchOfficeId)
  ), [qualifications]);

  const canEditAssignerSelection = (isOwnParticipant && !noOrgsAllowAssignerSelection) || isAuthorized(record, 'officialAvailabilities', 'assigners');

  const excludedIds = offices.filter(office => {
    const paths = office.path.split('.').map(Number);
    return !includedOfficeIds.some(officeId => paths.includes(officeId));
  }).map(office => office.id);

  return <>
    {isOwnParticipant && <AssignerSelectionAlert allOrgsAllowAssignerSelection={allOrgsAllowAssignerSelection} noOrgsAllowAssignerSelection={noOrgsAllowAssignerSelection} />}
    {canEditAssignerSelection && <AutocompleteInput
      choices={offices}
      getOptionDisabled={office => !isAssigner && excludedIds.includes(office.id)}
      source="officeIds"
      label="resources.participants.labels.availability.add_associations"
      placeholder="resources.participants.messages.availability.search_by_association"
      emptyOptionsText="resources.participants.messages.availability.enter_name_search"
      multiple
      hideDisabled
      disableClearable
      minFilterLength={3}
      groupBy={office => office.parent?.name || translate('ra.message.unknown')}
      variant="outlined"
      margin="normal"
      fullWidth
      disableRemove
      renderTags={() => null}
    />}
    <Selection offices={offices} excludedIds={excludedIds} canEdit={canEditAssignerSelection} isAssigner={isAssigner} />
  </>
}

export const OfficeList = ({ participantId, offices }) => {
  const translate = useTranslate();
  const isOwnParticipant = useIsOwnParticipant(participantId);

  return <>
    <DialogTitle>{translate(`resources.participants.labels.availability.assigner_availabilities.${isOwnParticipant ? 'own_profile' : 'other_profile'}.title`)}</DialogTitle>
    <DialogContent>
      <DialogContentText>
        {translate(`resources.participants.labels.availability.assigner_availabilities.${isOwnParticipant ? 'own_profile' : 'other_profile'}.select_associations`)}
      </DialogContentText>
      <OfficesForm participantId={participantId} offices={offices} />
    </DialogContent>
  </>
}
